<script>
import { mapGetters } from 'vuex';
import CallerInfo from './CallerInfo.vue';
import ConnectionQualityIndicator from './ConnectionQualityIndicator.vue';

export default {
  name: 'PhoneControlsCompact',
  components: {
    CallerInfo,
    ConnectionQualityIndicator,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      phoneConfigured: 'phone/configured',
      status: 'phone/getStatus',
      callDuration: 'phone/getCallDuration',
      dialingDuration: 'phone/getDialingDuration',
      phoneConversation: 'phone/getConversation',
    }),
    conversationId() {
      return this.phoneConversation?.id;
    },
    callerInfoText() {
      const contact = this.$store.state.phone.contact;
      const callerId = this.$store.state.phone.callerId;

      if (contact && contact.name) {
        return contact.name;
      }

      if (callerId) {
        return callerId;
      }

      return this.$store.state.phone.otherPartyNumber;
    },
    formattedCallDuration() {
      return this.formatDuration(this.callDuration);
    },
    formattedDialingDuration() {
      if (!this.dialingDuration) return '00:00';

      return this.formatDuration(this.dialingDuration);
    },
    showControls() {
      return (
        this.status === 'ringing' ||
        this.status === 'speaking' ||
        this.status === 'dialing'
      );
    },
  },
  methods: {
    formatDuration(duration) {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      const minStr = minutes.toString().padStart(2, '0');
      const secsStr = seconds.toString().padStart(2, '0');

      return `${minStr}:${secsStr}`;
    },
    answer() {
      this.$store.dispatch('phone/acceptCall');
    },
    hangup() {
      this.$store.dispatch('phone/hangup');
    },
    openConversation() {
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.phoneConversation.account_id,
          conversation_id: this.phoneConversation.id,
        },
      });
    },
  },
};
</script>

<template>
  <div
    v-if="phoneConfigured && showControls"
    id="phone-controls-compact"
    :class="{
      collapsed: collapsed,
      ringing: status === 'ringing',
      speaking: status === 'speaking',
      dialing: status === 'dialing',
    }"
  >
    <!-- Ringing state - incoming call -->
    <div v-if="status === 'ringing'" class="compact-controls ringing">
      <div v-if="!collapsed" class="caller-info-wrapper">
        <CallerInfo />
      </div>
      <div class="action-buttons" :class="{ collapsed: collapsed }">
        <button
          v-tooltip="
            collapsed
              ? {
                  content:
                    $t('PHONE_CTRLS.ACTIONS.ANSWER') + ' - ' + callerInfoText,
                  placement: 'right',
                }
              : { content: $t('PHONE_CTRLS.ACTIONS.ANSWER'), placement: 'top' }
          "
          class="phone-btn answer flashing"
          @click="answer"
        >
          <fluent-icon icon="call" size="20" />
        </button>
        <button
          v-tooltip="
            collapsed
              ? {
                  content:
                    $t('PHONE_CTRLS.ACTIONS.HANGUP') + ' - ' + callerInfoText,
                  placement: 'right',
                }
              : { content: $t('PHONE_CTRLS.ACTIONS.HANGUP'), placement: 'top' }
          "
          class="phone-btn hangup"
          @click="hangup"
        >
          <span class="hangup-icon">
            <fluent-icon icon="call" size="20" />
          </span>
        </button>
      </div>
    </div>

    <!-- Speaking state - active call -->
    <div
      v-if="status === 'speaking'"
      class="compact-controls speaking speaking-wrapper"
    >
      <span
        v-if="conversationId"
        v-tooltip.top="$t('PHONE_CTRLS.ACTIONS.OPEN_CONVERSATION')"
        class="open-conversation-icon"
        @click="openConversation"
      >
        <fluent-icon icon="chat" size="14" />
      </span>
      <div v-if="!collapsed" class="caller-info-wrapper">
        <CallerInfo />
        <span class="call-duration">{{ formattedCallDuration }}</span>
        <ConnectionQualityIndicator />
      </div>
      <div class="action-buttons" :class="{ collapsed: collapsed }">
        <button
          v-tooltip="
            collapsed
              ? {
                  content:
                    $t('PHONE_CTRLS.ACTIONS.HANGUP') + ' - ' + callerInfoText,
                  placement: 'right',
                }
              : { content: $t('PHONE_CTRLS.ACTIONS.HANGUP'), placement: 'top' }
          "
          class="phone-btn hangup"
          @click="hangup"
        >
          <span class="hangup-icon">
            <fluent-icon icon="call" size="20" />
          </span>
        </button>
      </div>
    </div>

    <!-- Dialing state - outgoing call -->
    <div v-if="status === 'dialing'" class="compact-controls dialing">
      <div v-if="!collapsed" class="caller-info-wrapper">
        <div>{{ $t('PHONE_CTRLS.STATUS.DIALING') }}</div>
        <CallerInfo />
        <span class="call-duration">{{ formattedDialingDuration }}</span>
      </div>
      <div class="action-buttons" :class="{ collapsed: collapsed }">
        <button
          v-tooltip="
            collapsed
              ? {
                  content:
                    $t('PHONE_CTRLS.ACTIONS.HANGUP') + ' - ' + callerInfoText,
                  placement: 'right',
                }
              : { content: $t('PHONE_CTRLS.ACTIONS.HANGUP'), placement: 'top' }
          "
          class="phone-btn hangup"
          @click="hangup"
        >
          <span class="hangup-icon">
            <fluent-icon icon="call" size="20" />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/_mixins.scss';

#phone-controls-compact {
  @include border-normal-top();
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  width: 100%;

  &.collapsed {
    &.ringing {
      min-height: 100px;
    }

    &.speaking,
    &.dialing {
      min-height: 50px;
    }
  }
}

.compact-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
}

.caller-info-wrapper {
  text-align: center;
  font-size: 14px;
  white-space: pre-line;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.call-duration {
  display: block;
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.dark .call-duration {
  color: #aaa;
}

.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;

  &.collapsed {
    flex-direction: column;
    gap: 10px;
  }
}

.phone-btn {
  @include background-white();
  border: 2px solid #a0a0a0;
  color: #a0a0a0;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
  }
}

.dark .phone-btn {
  background-color: #36393f;
  color: #dcddde;
}

.speaking-wrapper {
  position: relative;
}

.open-conversation-icon {
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  color: $success-color;
  display: inline-flex;

  &:hover {
    opacity: 0.7;
  }
}

.phone-btn:hover,
.ringing .phone-btn {
  &.answer {
    border: 2px solid #43b581;
    color: #43b581;
  }
  &.hangup {
    border: 2px solid #f04747;
    color: #f04747;
  }
}

.hangup-icon {
  transform: rotate(135deg);
}

.flashing {
  animation: flash 1s infinite;
}

@keyframes flash {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
